import { useCallback, useState } from "react";
import { useAuthStore } from "@/hooks/useAuthStore";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useBoundedWait } from "@/hooks/useBoundedWait";
import { reloadPage } from "@/utils/reloadPage";

function hasRbacFailure(auth: ReturnType<typeof useAuthStore>, orgId: string | undefined): boolean {
  return Boolean(
    orgId && auth.permissionsOrgId === orgId && auth.assignmentsOrgId === orgId && auth.rbacError
  );
}

function hasNoOrganization(context: ReturnType<typeof useOrganizationContext>): boolean {
  return (
    context.isReady &&
    !context.isLoading &&
    !context.error &&
    !context.currentOrganization?.id &&
    context.userOrganizations.length === 0
  );
}

/** Only successful permissions AND assignments for the selected org are authority. */
export function useRbacReadiness() {
  const auth = useAuthStore();
  const organization = useOrganizationContext();
  const { currentOrganization, isReady, error, refetch } = organization;
  const orgId = currentOrganization?.id;
  const ready = Boolean(isReady && orgId && auth.isRbacLoadedFor(orgId));
  const accessKey = `${auth.userId ?? "no-user"}:${auth.authEpoch}:${orgId ?? "no-organization"}`;
  const { failed: waitFailed, retry: resetWait } = useBoundedWait(accessKey, ready);
  const [retrying, setRetrying] = useState<string | null>(null);
  const knownRbacFailure = hasRbacFailure(auth, orgId);

  const retry = useCallback(() => {
    resetWait();
    if (!isReady) {
      reloadPage();
    } else if (orgId && !error) {
      setRetrying(orgId);
      void Promise.allSettled([auth.loadPermissions(orgId), auth.loadAssignments(orgId)]).then(() =>
        setRetrying((current) => (current === orgId ? null : current))
      );
    } else {
      void refetch().catch(() => undefined);
    }
  }, [auth, orgId, isReady, error, refetch, resetWait]);

  return {
    ready,
    failed: waitFailed || Boolean(error) || (knownRbacFailure && retrying !== orgId),
    retry,
    orgId,
    accessKey,
    noOrganization: hasNoOrganization(organization),
  };
}
